<template>
  <div>
    <Loader :messageSelector="messageSelector" :overlay="overlay" />
    <v-form ref="form" v-model="valid">
      <fieldset>
        <legend class="text-h6 text-sm-h5">Identification et coordonnées</legend>
        <div>
          <RessourceInfoBulle code="PI-RG-1" />
          <RessourceInfoBulle code="PI-RG-4" />

          <ContactDeBaseNew
            key="renseignementsGeneraux"
            ref="renseignementsGeneraux"
            :renseignementsGeneraux="renseignementsGeneraux"
            :rules="rules"
            disableDateNaissance
            disableNomPrenom
            @validate-form="validateForm"
          />

          <!-- Genre -->
          <v-container fluid>
            <p class="cssRequired">À quel genre vous identifiez-vous ?</p>
            <v-radio-group v-model="renseignementsGeneraux.genre" :rules="rules.genre" class="mt-3" @change="viderTitreDePreference" required>
              <v-radio v-for="genre in listeGenres" :key="genre.id" :label="`${genre.lang00}`" :value="genre" />
            </v-radio-group>
          </v-container>

          <!-- Titre de préférence-->
          <v-container fluid v-if="renseignementsGeneraux.genre?.id === $catalogues.genre.nonBinaire">
            <p class="cssRequired">Quel est votre titre de préférence ?</p>
            <v-radio-group v-model="renseignementsGeneraux.titreDePreference" :rules="rules.titreDePreference" class="mt-3" required>
              <v-radio
                v-for="titreDePreference in listeTitresDePreference"
                :key="titreDePreference.id"
                :label="`${titreDePreference.lang00}`"
                :value="titreDePreference"
              />
            </v-radio-group>
          </v-container>

          <!-- Sexe -->
          <v-container fluid>
            <p class="cssRequired">Indiquez votre sexe à la naissance</p>
            <v-radio-group v-model="renseignementsGeneraux.sexe" :rules="rules.sexe" class="mt-3" required>
              <v-radio v-for="sexe in listeSexes" :key="sexe.id" :label="`${sexe.lang00}`" :value="sexe"></v-radio>
            </v-radio-group>
            <p class="mb-6 warning-text">
              *Requis conformément aux exigences de l'Office des professions et pour la validation de votre identité par la Régie de l'assurance
              maladie du Québec, notamment afin d'autoriser l'accès des membres au registre des intervenants.
            </p>
          </v-container>

          <!-- PNI -->
          <v-container fluid>
            <p class="cssRequired">
              Je suis membre d'une Première Nation et Indien inscrit en vertu de la Loi sur les Indiens ou bénéficiaire en vertu d’un accord 
              sur les revendications territoriales (ex. Convention de la Baie-James et du Nord québécois).
            </p>
            <v-autocomplete
              v-model="renseignementsGeneraux.pni"
              :item-text="itemText"
              :item-value="itemValue"
              :items="listePni"
              :rules="rules.pni"
              class="cssRequired"
              label="Réponse"
              required
              return-object
              @change="resetNationsInuit()"
            ></v-autocomplete>
            <v-autocomplete v-if="isPremiereNation"
              v-model="renseignementsGeneraux.nationsInuit"
              :item-text="itemText"
              :item-value="itemValue"
              :items="listeNationsInuit"
              :rules="rules.notEmpty"
              class="cssRequired"
              label="Je suis : "
              required
              return-object
            ></v-autocomplete>
          </v-container>

          <!-- Citoyenneté -->
          <v-autocomplete
            v-model="renseignementsGeneraux.citoyennetes"
            :item-text="itemText"
            :item-value="itemValue"
            :items="listeCitoyennete"
            :rules="rules.citoyennetes"
            class="cssRequired"
            label="Citoyenneté(s)"
            multiple
            required
            return-object
          />
          <!-- Autre Citoyennete -->
          <v-text-field
            v-if="autreCitoyennete"
            v-model="renseignementsGeneraux.autresCitoyennetes"
            class="cssRequired"
            label="Autre citoyenneté"
            required
          />

          <!-- Informations liées à l'adresse -->
          <ContactDeBaseAddress
            key="renseignementsGenerauxAddress"
            ref="renseignementsGeneraux"
            :renseignementsGeneraux="renseignementsGeneraux"
            :rules="rules"
            @validate-form="validateForm"
          />
        </div>
      </fieldset>

      <!-- Courriel de correspondance ( potentiellement l'intégrer dans un composant si utilisé dans tout les forms) -->
      <fieldset>
        <legend class="text-h6 text-sm-h5">Courriel de correspondance</legend>
        <div>
          <RessourceInfoBulle code="PI-RG-2" />
          <!-- Email -->
          <v-text-field
            v-model="renseignementsGeneraux.courriel"
            :rules="rules.email"
            type="email"
            label="Courriel de correspondance*"
            required
            class="cssRequired"
          />
        </div>
      </fieldset>

      <!-- Langues -->
      <fieldset>
        <legend class="text-h6 text-sm-h5">Langue(s)</legend>
        <div>
          <RessourceInfoBulle code="PI-RG-3" />
          <v-simple-table v-if="langues.length">
            <thead>
              <tr>
                <th class="text-left">Langue(s) de pratique</th>
                <th class="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in langues" :key="index">
                <td class="pt-4">
                  <v-autocomplete
                    v-model="item.langue"
                    :item-text="itemText"
                    :item-value="itemValue"
                    :items="listeLangues"
                    :rules="rules.langue"
                    label="Langue"
                    required
                    return-object
                  />
                </td>
                <td class="text-right">
                  <v-btn icon @click="deleteRowLangue(item)">
                    <v-icon class="clickable redIcon">mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="mt-6">
            <v-btn color="primary" @click="ajouterLangue()" rul>Ajouter</v-btn>
            <v-input :value="langues" :rules="rules.langues"></v-input>
          </div>
        </div>
      </fieldset>

      <!-- Télépratique -->
      <fieldset>
        <legend class="text-h6 text-sm-h5">Télépratique</legend>

        <div class="legalSection">
          <v-list flat subheader>
            <v-list-item-group>
              <list-item-with-checkbox
                v-for="telepratiqueChoixItem in telepratiqueChoix"
                v-bind:key="telepratiqueChoixItem.id"
                :value="telepratiqueChoixItem.value"
                :texte="telepratiqueChoixItem.lang00"
                :disabled="telepratiqueChoixItem.disabled"
                :property-name="telepratiqueChoixItem.id.toString()"
                :radioMode="true"
                @updateChoix="updateCheckbox($event, telepratique, 'telepratiqueChoix', listeTelepratiqueChoix, telepratiqueChoix)"
              />
            </v-list-item-group>
          </v-list>
          <!-- Champs caché permettant d'appliquer une rule pour s'assurer qu'une valeur est renseignée -->
          <v-text-field
            v-model="this.telepratique.telepratiqueChoix"
            :rules="rules.notEmpty"
            label="Selected item"
            class="d-none"
          />
        </div>

        <div class="ml-10 legalSection" v-if="isTelepratiqueQuebec">
          <v-list flat subheader>
            <v-list-item-group>
              <list-item-with-checkbox
                v-for="telepratiqueAuQuebecItem in telepratiqueAuQuebec"
                v-bind:key="telepratiqueAuQuebecItem.id"
                :value="telepratiqueAuQuebecItem.value"
                :texte="telepratiqueAuQuebecItem.lang00"
                :disabled="telepratiqueAuQuebecItem.disabled"
                :property-name="telepratiqueAuQuebecItem.id.toString()"
                :radioMode="true"
                @updateChoix="updateCheckbox($event, telepratique, 'telepratiqueAuQuebec', listeTelepratiqueAuQuebec, telepratiqueAuQuebec)"
              />
            </v-list-item-group>
          </v-list>
          <!-- Champs caché permettant d'appliquer une rule pour s'assurer qu'une valeur est renseignée -->
          <v-text-field
            v-model="this.telepratique.telepratiqueAuQuebec"
            :rules="rules.notEmpty"
            label="Selected item"
            class="d-none"
          />
        </div>

      </fieldset>

    </v-form>
    <div class="mt-6 text-right justify-space-between">
      <v-btn :disabled="!canContinue" color="primary" @click="saveAndContinue()">Suivant</v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"
import ContactDeBaseNew from "../common/ContactDeBaseNew"
import ContactDeBaseAddress from "../common/ContactDeBaseAddress"
import Loader from "../Loader"
import NavigationPageFormulaireInscriptionMixin from "@/mixins/navigationPageFormulaireInscriptionMixin"
import ListItemWithCheckbox from "../common/ListItemWithCheckbox"
import ListItemWithCheckboxMixin from "@/mixins/listItemWithCheckboxMixin"

export default {
  mixins: [NavigationPageFormulaireInscriptionMixin, ListItemWithCheckboxMixin],
  components: {
    ContactDeBaseNew: ContactDeBaseNew,
    ContactDeBaseAddress: ContactDeBaseAddress,
    Loader: Loader,
    ListItemWithCheckbox
  },
  props: {
    routeName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      telepratiqueChoix: [],
      telepratiqueAuQuebec: [],
      overlay: false,
      messageSelector: "",
      rules: {
        firstName: [(v) => !!v || "Le prénom est requis"],
        lastName: [(v) => !!v || "Le nom de famille est requis"],
        birthday: [(v) => !!v || "La date de naissance est requise"],
        sexe: [(v) => !!v || "Le sexe est requis"],
        genre: [(v) => !!v || "Le genre est requis"],
        pni: [(v) => !!v || "Veuillez indiquer si vous êtes membre ou non d'une Première Nation."],
        titreDePreference: [(v) => !!v || "Le titre de préférence est requis lorsque le genre est non binaire"],
        dateDeNaissance: [(v) => !!v || "La date de naissance est requise"],
        citoyennetes: [(v) => (!!v && v.length != 0) || "La citoyenneté est requise"],
        paysDeNaissance: [(v) => (!!v && v.length != 0) || "Le pays de naissance est requis"],
        adresse: [(v) => !!v || "L'adresse est requise"],
        ville: [(v) => !!v || "La ville est requise"],
        provinceOuEtatSiApplicable: [(v) => !this.isProvinceRequired || (!!v && v.length != 0) || "La province ou l'état est requis"],
        pays: [(v) => (!!v && v.length != 0) || "Le pays est requis"],
        postalCode: [(v) => !!v || "Le code postal est requis"],
        telephone: [(v) => this.auMoinsUnTelValide || "Au moins un numéro de téléphone est requis"],
        cellulaire: [(v) => this.auMoinsUnTelValide || "Cette valeur est requise"],
        email: [(v) => !!v || "Le courriel est requis", (v) => this.validateEmail || "Le format du courriel est invalide"],
        langue: [(v) => (!!v && v.length != 0) || "La langue est requise"],
        langues: [(v) => v.length !== 0 || "La saisie d'une langue est requise"],
        notEmpty: [(v) => !!v || "Ce champs est requis"],
      },
      valid: true
    }
  },
  async created() {
    await this.fetchItems()
  },
  computed: {
    ...mapGetters({
      demandeInscriptionId: "DemandeInscription/GET_DEMANDEINSCRIPTIONID",
      renseignementsGeneraux: "RenseignementsGeneraux/getRenseignementsGeneraux",
      listeCitoyennete: "Catalogue/GET_CITOYENNETE",
      listeSexes: "Catalogue/GET_SEXE",
      listeGenres: "Catalogue/getGenre",
      listeTitresDePreference: "Catalogue/getTitresDePreference",
      listePni: "Catalogue/getPni",
      listeLangues: "Catalogue/GET_LANGUE",
      langues: "Langue/getLangues",
      telepratique: "Telepratique/GET_TELEPRATIQUE",
      listeTelepratiqueChoix: "Catalogue/getTelepratiqueChoix",
      listeTelepratiqueAuQuebec: "Catalogue/getTelepratiqueAuQuebec",
      listeNationsInuit: "Catalogue/getNationsInuit",
    }),
    auMoinsUnTelValide() {
      return (
        (this.renseignementsGeneraux.cellulaire != null && this.renseignementsGeneraux.cellulaire != "") ||
        (this.renseignementsGeneraux.telephoneResidence != null && this.renseignementsGeneraux.telephoneResidence != "")
      )
    },
    autrePays() {
      return this.renseignementsGeneraux.paysDeNaissance && this.renseignementsGeneraux.paysDeNaissance.id == 8168
    },
    autreCitoyennete() {
      return this.renseignementsGeneraux.citoyennetes && this.renseignementsGeneraux.citoyennetes.find((c) => c.id == 8172)
    },
    validateEmail() {
      var re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(this.renseignementsGeneraux.courriel)
    },
    genererTexteContactExistant() {
      return "Le courriel " + this.renseignementsGeneraux.courriel + " existe déjà.<br/> Veuillez vous connecter à votre compte."
    },
    canContinue() {
      if (!this.valid) {
        return false
      }
      return true
    },
    isProvinceRequired() {
      if (this.renseignementsGeneraux.pays) {
        return (
          (this.renseignementsGeneraux.pays.id === this.$catalogues.pays.canada ||
            this.renseignementsGeneraux.pays.id === this.$catalogues.pays.france ||
            this.renseignementsGeneraux.pays.id === this.$catalogues.pays.usa) &&
          this.renseignementsGeneraux.provinceOuEtatSiApplicable == null
        )
      } else {
        return false
      }
    },
    isTelepratiqueQuebec() {
      if(this.telepratique?.telepratiqueChoix?.id == null) {
        return false;
      }
      return this.telepratique.telepratiqueChoix.id == this.$catalogues.telepratiqueChoix.telepratiqueAuQuebec;
    },
    isPremiereNation() {
      if(this.renseignementsGeneraux?.pni?.id == null) {
        return false;
      }
      return this.renseignementsGeneraux.pni.id == this.$catalogues.pni.oui;
    },
  },
  methods: {
    ...mapMutations({
      setSexe: "RenseignementsGeneraux/setSexe",
      setGenre: "RenseignementsGeneraux/setGenre",
      setTitreDePreference: "RenseignementsGeneraux/setTitreDePreference",
      setDemandeInscriptionId: "DemandeInscription/SET_ID",
      setContactId: "DemandeInscription/SET_CONTACTID",
      addLangue: "Langue/addLangue",
      removeLangue: "Langue/removeLangue"
    }),
    ...mapActions({
      getCatalogue: "Catalogue/getCatalogue",
      getRenseignementsGenerauxFromInscription: "RenseignementsGeneraux/getRenseignementsGenerauxFromInscription",
      updateDemandeAdmission: "DemandeAdmission/updateDemandeAdmission",
      updateRenseignementsGenerauxFromInscription: "RenseignementsGeneraux/updateRenseignementsGenerauxFromInscription",
      createDemandeInscription: "DemandeInscription/createDemandeInscription",
      createLangue: "Langue/createLangue",
      updateLangue: "Langue/updateLangue",
      deleteLangue: "Langue/deleteLangue",
      fetchLangueByDemandeInscriptionId: "Langue/fetchLangueByDemandeInscriptionId",
      getTelepratiqueByDemandeInscriptionId: "Telepratique/getTelepratiqueByDemandeInscriptionId",
      updateTelepratique: "Telepratique/updateTelepratique",
    }),
    async fetchItems() {
      try {
        this.messageSelector = "Loading"
        this.overlay = true

        const requests = [
          this.getCatalogue(this.$cataloguesDesc.titreDePreference),
          this.getCatalogue(this.$cataloguesDesc.paysOrigine),
          this.getCatalogue(this.$cataloguesDesc.citoyennete),
          this.getCatalogue(this.$cataloguesDesc.sexe),
          this.getCatalogue(this.$cataloguesDesc.genre),
          this.getCatalogue(this.$cataloguesDesc.langue),
          this.getCatalogue(this.$cataloguesDesc.pni),
          this.getCatalogue(this.$cataloguesDesc.telepratiqueChoix),
          this.getCatalogue(this.$cataloguesDesc.telepratiqueAuQuebec),
          this.getCatalogue(this.$cataloguesDesc.nationsInuit),
        ]

        let inscriptionId = this.$route.query.demandeInscriptionId
        // Si on a pas de Id de demande inscription, on en crée une
        if (inscriptionId == null) {
          let typeInscription = { id: 0 }
          typeInscription.id = process.env.VUE_APP_TYPEINSCRIPTION_PREMIEREINSCRIPTION
          inscriptionId = await this.createDemandeInscription({
            contactEncryptedId: this.$route.query.contactId,
            typeInscription: typeInscription
          })
        }
        this.setContactId(this.$route.query.contactId)
        this.setDemandeInscriptionId(inscriptionId)

        if (inscriptionId) {
          if (!this.$route.query.demandeInscriptionId) {
            this.$router.push({
              name: this.$route.name,
              query: { ...this.$route.query, demandeInscriptionId: inscriptionId }
            })
          }

          requests.push(this.getRenseignementsGenerauxFromInscription(inscriptionId))
          requests.push(this.fetchLangueByDemandeInscriptionId(inscriptionId))
          requests.push(this.getTelepratiqueByDemandeInscriptionId(inscriptionId))

          await Promise.all(requests)

          // hack pour l'affichage du radio
          this.setSexe(this.listeSexes.find((s) => s.id === this.renseignementsGeneraux?.sexe?.id))
          this.setGenre(this.listeGenres.find((s) => s.id === this.renseignementsGeneraux?.genre?.id))
          this.setTitreDePreference(this.listeTitresDePreference.find((s) => s.id === this.renseignementsGeneraux?.titreDePreference?.id))
          this.$refs.form.resetValidation()

          this.setupCheckbox(this.telepratique.telepratiqueChoix, this.listeTelepratiqueChoix, this.telepratiqueChoix);
          this.setupCheckbox(this.telepratique.telepratiqueAuQuebec, this.listeTelepratiqueAuQuebec, this.telepratiqueAuQuebec);

          this.validateForm()
        }
        this.overlay = false
      } catch (erreur) {
        console.log(erreur)
      }
    },
    itemText: (item) => item.lang00,
    itemValue: (item) => item.id,
    saveAndContinue() {
      this.save()
    },
    async save() {
      this.validateForm()
      if (this.valid) {
        this.messageSelector = "Creation"
        this.overlay = true

        const requests = [
          this.updateRenseignementsGenerauxFromInscription({
            id: this.demandeInscriptionId,
            ...this.renseignementsGeneraux
          })
        ]

        for (const element of this.langues) {
          if (!element.id) {
            requests.push(this.createLangue(element))
          } else {
            requests.push(this.updateLangue(element))
          }
        }
        
        requests.push(this.updateTelepratique(this.telepratique));
        
        await Promise.all(requests)

        this.next()
      }
      this.overlay = false
    },
    validateForm() {
      this.valid = this.$refs.form.validate()
    },

    modelChange() {
      this.$nextTick(() => {
        this.model = null
      })
      this.validateForm()
    },
    ajouterLangue() {
      if (!(this.listeLangues && this.listeLangues.length > 0)) {
        return // catalogue langue vide
      }
      this.addLangue({
        id: null,
        pp: this.$route.query.contactId,
        demandeInscription: this.demandeInscriptionId
      })
    },
    viderTitreDePreference() {
      this.renseignementsGeneraux.titreDePreference = null
    },
    async deleteRowLangue(item) {
      if (!item.id) {
        this.removeLangue(item)

        return
      }

      this.messageSelector = "Loading"
      this.overlay = true

      await this.deleteLangue(item)

      this.overlay = false
    },
    resetNationsInuit(){
      this.renseignementsGeneraux.nationsInuit = null;
    }
  }
}
</script>

<style></style>